// Only settings attributed to this child. Missing fields retain what was
// reported; an explicit null clears it. Parent settings are never a fallback.
type Json = Record<string, unknown>
const record = (value: unknown): Json => typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Json : {}
const value = (value: unknown): string | null => typeof value === 'string' && value.trim() !== '' && value.trim().toLowerCase() !== 'default' ? value.trim() : null

export interface EngineSettings {
  model?: string | null
  effort?: string | null
}

/** Child-scoped ACP settings, with labels supplied by that child. */
export function childSettings(before: EngineSettings | undefined, update: Json): EngineSettings | undefined {
  if (update.sessionUpdate === 'current_model_update') return {...before, model: value(update.currentModelId)}
  if (update.sessionUpdate === 'session_info_update') return reportedSettings(before, update)
  if (update.sessionUpdate !== 'config_option_update' || !Array.isArray(update.configOptions)) return before
  const options = update.configOptions.map(record)
  const selected = (category: string[]) => {
    const option = options.find(option => category.includes(String(option.category)))
    const current = value(option?.currentValue)
    if (!current) return null
    const choices = Array.isArray(option?.options) ? option.options.flatMap(choice => {
      const item = record(choice)
      return Array.isArray(item.options) ? item.options.map(record) : [item]
    }) : []
    return value(choices.find(choice => choice.value === current)?.name) ?? current
  }
  return {...before, model: selected(['model']), effort: selected(['thought_level', 'thinking'])}
}

/** Launch input, then the resolved model from Claude's structured result. */
export function launchSettings(before: EngineSettings | undefined, input: Json, response: Json): EngineSettings {
  const resolved = value(response.resolvedModel)
  const model = before?.model ?? value(input.model)
  // Claude's fork ignores an effort passed to Agent; no child effort was chosen.
  const effort = value(input.effort) ?? value(input.reasoning_effort) ?? value(input.reasoningEffort)
  return {...before, model: resolved ?? model, effort: input.subagent_type === 'fork' ? null : effort ?? before?.effort ?? null}
}

/**
 * A native Codex child's resolved model and effort, which Poulpe's codex-acp extension sends in that child's own
 * session (crates/poulpe-runner/src/acp/codex_access.mjs). A field it leaves out keeps what was reported.
 */
function reportedSettings(before: EngineSettings | undefined, update: Json): EngineSettings | undefined {
  const reported = record(record(record(update._meta).poulpe).subagent)
  if (reported.version !== 1) return before
  const model = value(reported.model)
  const effort = value(reported.effort)
  if ((!model || model === before?.model) && (!effort || effort === before?.effort)) return before
  return {...before, ...(model ? {model} : {}), ...(effort ? {effort} : {})}
}
